import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles.css';
import { API_URL } from '../config';

function AccountSettings() {
  const [user, setUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [location, setLocation] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await axios.get(`${API_URL}/api/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setUser(response.data);
      setNewEmail(response.data.email);
      if (response.data.location) setLocation(response.data.location);
    } catch (err) {
      setError('Failed to load user data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setError('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message) => {
    setError(message);
    setSuccessMessage('');
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      showError('Email cannot be empty');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/user/email`, 
        { newEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setUser({ ...user, email: newEmail });
      showSuccess('Email updated successfully!');
    } catch (err) {
      showError('Failed to update email: ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      showError('Current password is required');
      return;
    }

    if (!newPassword) {
      showError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      showError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/user/password`, 
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      showSuccess('Password updated successfully!');
    } catch (err) {
      showError('Failed to update password: ' + (err.response?.data?.error || err.message));
    }
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="spinner">Loading account settings...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <nav className="dashboard-nav">
        <button onClick={() => window.location.href = '/dashboard'}>Dashboard</button>
        <button onClick={() => window.location.href = '/question-of-the-day'}>Question of the Day</button>
        <button onClick={() => window.location.href = '/ai-assistant'}>AI Assistant</button>
        <button onClick={() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }}>
          Logout
        </button>
      </nav>

      <h1>Account Settings</h1>
      
      {error && <div className="error">{error}</div>}
      {successMessage && <div className="success">{successMessage}</div>}

      {user && (
        <div className="account-info" style={{ marginBottom: '30px' }}>
          <h2>Account Information</h2>
          <p><strong>Current Email:</strong> {user.email}</p>
          <p><strong>Account Type:</strong> {user.is_admin ? 'Administrator' : 'User'}</p>
        </div>
      )}

      {/* Location Section */}
      <div className="settings-section" style={{
        marginBottom: '40px',
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <h2>Your Location</h2>
        <p style={{ color: '#666', marginTop: 0 }}>
          Used to find nearby restaurants, venues, and experiences when you ask the AI Assistant for suggestions.
        </p>
        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/user/location`,
              { location },
              { headers: { Authorization: `Bearer ${token}` }}
            );
            showSuccess('Location saved!');
          } catch (err) {
            showError('Failed to save location: ' + (err.response?.data?.error || err.message));
          }
        }}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="location" style={{ display: 'block', marginBottom: '5px' }}>
              City, State or Zip Code:
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Louisville, KY or 40241"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
          </div>
          <button type="submit" className="btn-form">Save Location</button>
        </form>
      </div>

      {/* Email Update Section */}
      <div className="settings-section" style={{ 
        marginBottom: '40px',
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <h2>Update Email Address</h2>
        <form onSubmit={handleEmailUpdate}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="newEmail" style={{ display: 'block', marginBottom: '5px' }}>
              New Email Address:
            </label>
            <input
              id="newEmail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email address"
              style={{ 
                width: '100%', 
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              required
            />
          </div>
          <button 
            type="submit"
            className="btn-form"
          >
            Update Email
          </button>
        </form>
      </div>

      {/* Password Update Section */}
      <div className="settings-section" style={{ 
        marginBottom: '40px',
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <h2>Change Password</h2>
        <form onSubmit={handlePasswordUpdate}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="currentPassword" style={{ display: 'block', marginBottom: '5px' }}>
              Current Password:
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              style={{ 
                width: '100%', 
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              required
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '5px' }}>
              New Password:
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              style={{ 
                width: '100%', 
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              minLength={8}
              required
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '5px' }}>
              Confirm New Password:
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{ 
                width: '100%', 
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '16px'
              }}
              required
            />
          </div>
          
          <button 
            type="submit"
            className="btn-form"
          >
            Update Password
          </button>
        </form>
      </div>

      {/* Security Note */}
      <div className="security-note" style={{ 
        padding: '15px',
        backgroundColor: '#e3f2fd',
        border: '1px solid #90caf9',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: '0', color: '#1976d2' }}>Security Tips</h3>
        <ul style={{ marginBottom: '0' }}>
          <li>Use a strong password with at least 8 characters</li>
          <li>Include uppercase letters, lowercase letters, numbers, and symbols</li>
          <li>Don't reuse passwords from other accounts</li>
          <li>Consider using a password manager</li>
        </ul>
      </div>
    </div>
  );
}

export default AccountSettings;